import type { PostgrestError } from '@supabase/supabase-js';
import { getProdSupabaseClient } from './client';
import { callProdRpc, getTenantId } from './dealers';
import { isRpcError, SESSION_ACCESS_MESSAGE } from './rpcErrors';
import { translateSystemError } from '@/lib/utils/errorTranslator';

/**
 * Dealer settings (include/exclude + notes) are tenant-level and keyed by the
 * canonical dealer id. `set_dealer_config` is the ONLY write path: it writes
 * every spelling of each dealer for every user in the tenant and logs each
 * write server-side. Never write dealer rows of prod.user_preferences directly.
 * Contract: docs/technical_docs/DEALER_IDENTITY_DEDUP_SPEC.md §7.
 */

/** Only the keys present are changed; '' clears a note. */
export interface DealerConfigPatch {
  isIncluded?: boolean;
  notes?: string;
  advisorNotes?: string;
  technicianNotes?: string;
}

/** State of one dealer after the write, as returned by set_dealer_config. */
export interface DealerConfigRow {
  dealer_id: string;
  alias_names: string[];
  is_included: boolean;
  notes: string | null;
  advisor_notes: string | null;
  technician_notes: string | null;
  /** Other dealers listed under an identical name, so the write reached them too. */
  also_affects: string[];
}

export const SET_DEALER_CONFIG_MAX_IDS = 5000;

export type DealerConfigErrorKind = 'session' | 'permission' | 'stale' | 'invalid' | 'unknown';

export const DEALER_CONFIG_ERROR_MESSAGES: Record<DealerConfigErrorKind, string> = {
  session: SESSION_ACCESS_MESSAGE,
  permission: "You don't have permission to change dealer settings.",
  stale: 'The dealer list was out of date and has been refreshed. Please try again.',
  invalid: 'Dealer settings could not be saved. Please try again.',
  unknown: 'Dealer settings could not be saved. Please try again.'
};

export class DealerConfigError extends Error {
  constructor(
    readonly kind: DealerConfigErrorKind,
    message: string = DEALER_CONFIG_ERROR_MESSAGES[kind]
  ) {
    super(message);
    this.name = 'DealerConfigError';
  }
}

/** Maps PT401 / PT404 / PT422 (field in `details`) to an error the UI can act on. */
export function classifyDealerConfigError(error: PostgrestError): Error {
  if (isRpcError(error, 'PT401', 'permission')) return new DealerConfigError('permission');
  if (isRpcError(error, 'PT401')) return new DealerConfigError('session');
  if (isRpcError(error, 'PT404')) return new DealerConfigError('stale');
  if (isRpcError(error, 'PT422')) {
    // Empty list, > 5000 ids or nothing to change: a bug on our side.
    // eslint-disable-next-line no-console
    console.error('[set_dealer_config] rejected request:', error);
    return new DealerConfigError('invalid');
  }
  return translateSystemError(error, 'saving dealer settings');
}

export function toRpcArgs(dealerIds: string[], patch: DealerConfigPatch) {
  return {
    p_dealer_ids: dealerIds,
    ...(patch.isIncluded !== undefined && { p_is_included: patch.isIncluded }),
    ...(patch.notes !== undefined && { p_notes: patch.notes }),
    ...(patch.advisorNotes !== undefined && { p_advisor_notes: patch.advisorNotes }),
    ...(patch.technicianNotes !== undefined && { p_technician_notes: patch.technicianNotes })
  };
}

export function chunkIds(ids: string[], size: number = SET_DEALER_CONFIG_MAX_IDS): string[][] {
  const chunks: string[][] = [];
  for (let start = 0; start < ids.length; start += size) {
    chunks.push(ids.slice(start, start + size));
  }
  return chunks;
}

/**
 * Writes `patch` to every dealer in `dealerIds`. Larger selections are sent in
 * chunks of 5000, one after another (the server serialises per tenant anyway).
 * If a chunk fails, later chunks are not sent and earlier ones stay written.
 */
export async function setDealerConfig(
  dealerIds: string[],
  patch: DealerConfigPatch
): Promise<DealerConfigRow[]> {
  const prodClient = getProdSupabaseClient();
  const rows: DealerConfigRow[] = [];
  for (const chunk of chunkIds(dealerIds)) {
    // Sequential on purpose: parallel calls would only queue on the tenant lock.
    const { data, error } = await callProdRpc<DealerConfigRow[]>(
      prodClient,
      'set_dealer_config',
      toRpcArgs(chunk, patch)
    );
    if (error) throw classifyDealerConfigError(error);
    rows.push(...(data ?? []));
  }
  return rows;
}

/** OEMs of the tenant's zones (e.g. ['Ford', 'Hyundai']), ordered. */
export async function getTenantOems(): Promise<string[]> {
  const tenantId = await getTenantId();
  if (!tenantId) return [];
  const { data, error } = await callProdRpc<Array<{ oem: string }>>(
    getProdSupabaseClient(),
    'get_tenant_oems',
    { p_tenant_id: tenantId }
  );
  if (error) throw translateSystemError(error, 'loading tenant OEMs');
  return (data ?? []).map(row => row.oem);
}

/** Trimmed spelling → canonical dealer id, for screens that only know a dealer by name. */
export function buildAliasIndex(
  dealers: Array<{ dealer_id: string; alias_names: string[] }>
): Map<string, string> {
  const index = new Map<string, string>();
  dealers.forEach(dealer => {
    dealer.alias_names.forEach(name => {
      const key = name.trim();
      if (key && !index.has(key)) index.set(key, dealer.dealer_id);
    });
  });
  return index;
}
